import { useNavigate } from 'react-router-dom';
import { Avatar, Badge, Bar, ConsolePage, DStat, EmptyState, Icon, Panel, QueryState, ListSkeleton, type IconName } from '@/components';
import { useCourses, useRoster } from '@/hooks/queries';
import { initialsOf } from '@/lib/format';
import type { RosterEntry } from '@/types/domain';

function CertChip({ state }: { state: RosterEntry['cert'] }) {
  if (state === 'issued') return <Badge kind="sage" dot>Issued</Badge>;
  if (state === 'ready') return <Badge kind="ochre" dot>Ready</Badge>;
  return <Badge kind="gray">In progress</Badge>;
}

export function InstructorDashboard() {
  const navigate = useNavigate();
  const { data: courses = [] } = useCourses();
  const primary = courses[0];
  const rosterQuery = useRoster(primary?.id);
  const roster = rosterQuery.data ?? [];

  // Real aggregates from the live roster.
  const enrolled = roster.length;
  const lessonsDone = roster.filter((r) => r.lessons >= 100).length;
  const attended = roster.filter((r) => r.attended).length;
  const postAttempted = roster.filter((r) => r.posttest != null).length;
  const postPass = roster.filter((r) => r.posttest != null && r.posttest >= 80).length;
  const passRate = postAttempted ? Math.round((postPass / postAttempted) * 100) : 0;
  const certReady = roster.filter((r) => r.cert === 'ready').length;
  const certIssued = roster.filter((r) => r.cert === 'issued').length;
  const pctOf = (n: number) => (enrolled ? Math.round((n / enrolled) * 100) : 0);
  // Seats filled vs capacity — so the "Enrolled" bar shows real utilization, not a constant 100%.
  const capacityPct = primary?.capacity ? Math.min(100, Math.round((enrolled / primary.capacity) * 100)) : 100;

  const funnel: [string, number, number][] = [
    ['Enrolled', enrolled, capacityPct],
    ['Completed lessons', lessonsDone, pctOf(lessonsDone)],
    ['Attended onsite', attended, pctOf(attended)],
    ['Passed post-test', postPass, pctOf(postPass)],
    ['Certificate issued', certIssued, pctOf(certIssued)],
  ];

  const attention: { ic: IconName; t: string; s: string; tone: string }[] = [];
  if (certReady) attention.push({ ic: 'award', t: `${certReady} certificate${certReady > 1 ? 's' : ''} ready`, s: 'All requirements met — pending issue', tone: 'ochre' });
  if (enrolled - attended > 0) attention.push({ ic: 'qr', t: `${enrolled - attended} not checked in`, s: primary?.seminar ? `Onsite · ${primary.seminar}` : 'Onsite session', tone: 'clay' });
  if (postAttempted - postPass > 0) attention.push({ ic: 'doc', t: `${postAttempted - postPass} below pass score`, s: 'Post-test under 80%', tone: 'slate' });

  return (
    <ConsolePage title="Dashboard" th="แดชบอร์ดผู้สอน" crumbs="TEACHING">
      <QueryState query={rosterQuery} skeleton={<ListSkeleton rows={5} />} isEmpty={() => !primary} emptyIcon="book" emptyTitle="No course assigned yet">
        {() => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 14 }}>
              <DStat label="Courses" value={String(courses.length)} icon="book" sub={primary ? primary.title.en : '—'} onClick={() => navigate('/teach/courses')} />
              <DStat label="Enrolled" value={String(enrolled)} icon="users" tone="var(--spark)" sub={primary?.capacity ? `of ${primary.capacity} seats` : 'learners'} onClick={() => navigate('/teach/learners')} />
              <DStat label="Post-test pass rate" value={`${passRate}%`} icon="chart" tone="var(--sage)" sub={`${postPass} of ${postAttempted} attempts`} onClick={() => navigate('/teach/analytics')} />
              <DStat label="Certificates" value={String(certIssued)} icon="award" tone="var(--ochre)" sub={certReady ? `${certReady} ready to issue` : 'issued'} onClick={() => navigate('/teach/cert-preview')} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.6fr) minmax(0,1fr)', gap: 18 }} className="max-md:!grid-cols-1">
              <Panel title={primary ? `${primary.title.en} · ${primary.code}` : 'Cohort'} action={<button className="btn btn-quiet btn-sm" onClick={() => navigate('/teach/learners')}>View learners</button>} pad={false}>
                <div style={{ padding: '14px 18px', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
                  {([['Lessons done', `${pctOf(lessonsDone)}%`, 'sage'], ['Attendance', `${attended}/${enrolled}`, 'slate'], ['Post-test', `${passRate}%`, 'sage'], ['Cert-ready', String(certReady), 'ochre']] as const).map((m, i) => (
                    <div key={i}>
                      <div className="eyebrow" style={{ marginBottom: 7 }}>{m[0]}</div>
                      <div style={{ fontSize: 22, fontWeight: 600, color: `var(--${m[2]})` }}>{m[1]}</div>
                    </div>
                  ))}
                </div>
                <div style={{ borderTop: '1px solid var(--line)', padding: '14px 18px' }}>
                  <div className="eyebrow" style={{ marginBottom: 12 }}>Cohort funnel</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {funnel.map((f, i) => (
                      <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <span style={{ width: 130, fontSize: 13, color: 'var(--ink-2)' }}>{f[0]}</span>
                        <div style={{ flex: 1 }}><Bar value={f[2]} /></div>
                        <span className="u-mono" style={{ width: 34, fontSize: 12.5, color: 'var(--ink-3)', textAlign: 'right' }}>{f[1]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Panel>

              <Panel title="Needs attention" pad={false}>
                {attention.length === 0 ? (
                  <div style={{ padding: 24 }}><EmptyState icon="checkCircle" title="All caught up" hint="No learners need attention right now." /></div>
                ) : (
                  <div className="divide">
                    {attention.map((x, i) => (
                      <button key={i} className="dash-row" onClick={() => navigate('/teach/learners')} style={{ all: 'unset', cursor: 'pointer', display: 'block' }}>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '14px 18px' }}>
                          <div style={{ width: 34, height: 34, borderRadius: 9, flex: 'none', background: `var(--${x.tone}-soft)`, color: `var(--${x.tone})`, display: 'grid', placeItems: 'center' }}>
                            <Icon name={x.ic} size={17} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13.5, fontWeight: 500 }}>{x.t}</div>
                            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 1 }}>{x.s}</div>
                          </div>
                          <Icon name="chevR" size={16} style={{ color: 'var(--ink-4)' }} />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </Panel>
            </div>

            <Panel title={`Learners · ${enrolled}`} action={<button className="btn btn-quiet btn-sm" onClick={() => navigate('/teach/learners')}>View all</button>} pad={false}>
              {roster.length === 0 ? (
                <div style={{ padding: 24 }}><EmptyState icon="users" title="No learners enrolled yet" /></div>
              ) : (
                <div className="divide">
                  {roster.slice(0, 6).map((r, i) => (
                    <button key={i} className="dash-row" onClick={() => navigate('/teach/learners')} style={{ all: 'unset', display: 'flex', gap: 12, alignItems: 'center', padding: '12px 18px', cursor: 'pointer', width: '100%', boxSizing: 'border-box' }}>
                      <Avatar initials={initialsOf(r.name.split(' ')[0], r.name.split(' ')[1] ?? '')} size={32} tone={r.tone ?? 'slate'} photoUrl={r.photoUrl} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 500 }}>{r.name || r.th}</div>
                        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{r.unit}</div>
                      </div>
                      <div style={{ width: 120, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1 }}><Bar value={r.lessons} /></div>
                        <span className="u-mono" style={{ fontSize: 12, color: 'var(--ink-3)', width: 34, textAlign: 'right' }}>{r.lessons}%</span>
                      </div>
                      <CertChip state={r.cert} />
                    </button>
                  ))}
                </div>
              )}
            </Panel>
          </div>
        )}
      </QueryState>
      <style>{`.dash-row { transition: background .12s; border-radius: 8px; } .dash-row:hover { background: var(--card-2); }`}</style>
    </ConsolePage>
  );
}
